import { Stack, Text } from "@mantine/core";
import { DataResult, EntityClientAction, FakeProgressBar, useExecuteProc } from "@mcurros2/microm";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { useEffect, useState } from "react";
import { API_URL, APP_ID } from "../../config";
import logoUrl from '../../logo.png';
import { Reportes } from "./Reportes";

async function embedImageFromUrl(pdf: PDFDocument, url: string) {
    try {
        const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
        if (!res.ok) return undefined;
        const ct = (res.headers.get('content-type') || '').toLowerCase();
        const bytes = await res.arrayBuffer();

        if (ct.includes('png')) return await pdf.embedPng(bytes);
        if (ct.includes('jpeg') || ct.includes('jpg')) return await pdf.embedJpg(bytes);

        return undefined;
    } catch {
        return undefined;
    }
}

async function buildPdf(dr: DataResult) {
    const A4 = { w: 595, h: 842 }, margin = 36, renglon = 24, headerH = 64;

    const pdf = await PDFDocument.create();
    const font = await pdf.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdf.embedFont(StandardFonts.HelveticaBold); // para “MTS”

    const headers: string[] = (dr as any)?.Header ?? [];
    const rows: any[][] = (dr as any)?.records ?? [];

    const idxImagen = headers.includes('ValorImagen') ? headers.indexOf('ValorImagen') : 0;
    const idxCliente = headers.includes('Cliente') ? headers.indexOf('Cliente') : 0;
    const idxCadena = headers.includes('Cadena') ? headers.indexOf('Cadena') : 0;
    const idxTienda = headers.includes('Tienda') ? headers.indexOf('Tienda') : 0;
    const idxRealizado = headers.includes('FechaRealizado') ? headers.indexOf('FechaRealizado') : 0;
    const idxDomicilio = headers.includes('Domicilio') ? headers.indexOf('Domicilio') : 0;

    if (!rows.length) {
        const page = pdf.addPage([A4.w, A4.h]);

        const titleY = A4.h - headerH - margin - 24;
        page.drawText('Sin datos', { x: margin, y: titleY, size: 18, font });
        return pdf.save();
    }

    const logoBytes = await fetch(logoUrl as string).then(r => r.arrayBuffer());
    const logoImg = await pdf.embedPng(logoBytes);
    const logoH = 40;                                
    const logoScale = logoH / logoImg.height;
    const logoW = logoImg.width * logoScale;

    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const imagen = String(row[idxImagen] ?? '');
        const cliente = String(row[idxCliente] ?? '');
        const cadena = String(row[idxCadena] ?? '');
        const tienda = String(row[idxTienda] ?? '');
        const realizado = String(row[idxRealizado] ?? '');
        const domicilio = String(row[idxDomicilio] ?? '');

        const page = pdf.addPage([A4.w, A4.h]);

        page.drawRectangle({
            x: 0, y: A4.h - headerH, width: A4.w, height: headerH, color: rgb(1, 0.6, 0)
        });

        const logoY = A4.h - headerH + (headerH - logoH) / 2;

        page.drawImage(logoImg, {
            x: margin,
            y: logoY,
            width: logoW,
            height: logoH
        });

        const titleY = A4.h - headerH - margin - 24;

        page.drawText(`Cliente: ${cliente}`, { x: margin, y: titleY, size: 12, font });
        page.drawText(`Tienda: ${cadena} - ${tienda}`, { x: margin, y: titleY - renglon, size: 12, font });
        page.drawText(`Domicilio: ${domicilio}`, { x: margin, y: titleY - renglon * 2, size: 12, font });
        page.drawText(`Fecha: ${realizado}`, { x: margin, y: titleY - renglon * 3, size: 12, font });

        const url_img = imagen ? `${API_URL}/${APP_ID}/serve/${imagen}` : '';
        let photoImg;
        if (imagen) photoImg = await embedImageFromUrl(pdf, url_img);

        const gap = 20;
        const imgTop = titleY - gap - renglon * 3;

        if (photoImg) {
            const maxW = A4.w - margin * 2, maxH = 420;
            const scale = Math.min(maxW / photoImg.width, maxH / photoImg.height);
            const imgW = photoImg.width * scale, imgH = photoImg.height * scale;
            page.drawImage(photoImg, { x: margin, y: imgTop - imgH, width: imgW, height: imgH });
        } else {
            page.drawText('Imagen no disponible', { x: margin, y: imgTop - 14, size: 12, font, color: rgb(0.5, 0.5, 0.5) });
        }

        const pn = `Página ${i + 1} de ${rows.length}`;
        page.drawText(pn, { x: A4.w - margin - 100, y: 16, size: 10, font });
    }

    return pdf.save();
}

export const ExportarPdfModal = ({ reportes, onClose }: { reportes: Reportes; onClose: () => void }) => {
    const getReportes = useExecuteProc(reportes, reportes.def.procs.repo_qryReportesFotos);
    const [datos0, setDatos0] = useState<DataResult>();

    useEffect(() => {
        (async () => {
            await getReportes.execute({
                c_cliente_id: reportes.def.columns.c_cliente_id.value,
                dt_fecha_desde: reportes.parentKeys.dt_fecha_desde,
                dt_fecha_hasta: reportes.parentKeys.dt_fecha_hasta,
                c_autorizacion_directa_status_id: reportes.parentKeys.c_autorizacion_directa_status_id,
                dt_fecha_realizado_desde: reportes.parentKeys.dt_fecha_realizado_desde,
                dt_fecha_realizado_hasta: reportes.parentKeys.dt_fecha_realizado_hasta,
            });
        })();
    }, [
        reportes.def.columns.c_cliente_id.value,
        reportes.parentKeys.dt_fecha_desde,
        reportes.parentKeys.dt_fecha_hasta,
        reportes.parentKeys.c_autorizacion_directa_status_id,
        reportes.parentKeys.dt_fecha_realizado_desde,
        reportes.parentKeys.dt_fecha_realizado_hasta
    ]);

    useEffect(() => {
        if (!getReportes.status.loading && getReportes.status.data) {
            setDatos0(getReportes.status.data[0]);
        }
    }, [getReportes.status]);

    useEffect(() => {
        (async () => {
            if (!datos0) return;
            try {
                const bytes = await buildPdf(datos0);
                const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: "application/pdf" }));
                window.open(url, "_blank");
            } finally {
                onClose();
            }
        })();
    }, [datos0]);

    return (
        <Stack>
            <Text>Generando PDF…</Text>
            <FakeProgressBar />
        </Stack>
    );
};

export const ACTExportarPdf: EntityClientAction = {
    name: "ACTExportarPdf",
    label: "Exportar Fotos",
    title: <Text fw={700}>Exportar Fotos</Text>,
    refreshOnClose: true,
    dontRequireSelection: true,
    showActionInViewMode: true,
    views: ["repo_brwBaseReportesDetallada"],
    onClick: async ({ entity, modal, element }) => {
        const reportes = entity as Reportes;
        if (!modal) {
            console.warn("modal context not found");
            return false;
        }
        await modal.open({
            modalProps: { title: <Text fw="700">Exportar Fotos</Text> },
            focusOnClosed: element,
            content: <ExportarPdfModal reportes={reportes} onClose={() => modal.close()} />,
        });
        return false;
    },
};